import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { useTranslation } from "react-i18next";
import { Lock, ShieldCheck, Plus, Eye, EyeOff } from "lucide-react";

function SecretValueInput({ name, value, onChange }) {
    const [show, setShow] = useState(false);
    return (
        <div className="space-y-1">
            <Label className="text-xs font-mono">{name}</Label>
            <div className="relative">
                <Input
                    type={show ? "text" : "password"}
                    placeholder="Enter secret value..."
                    value={value}
                    onChange={(e) => onChange(name, e.target.value)}
                    className="pr-9 font-mono text-sm"
                />
                <button
                    type="button"
                    onClick={() => setShow(!show)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded"
                >
                    {show ? <EyeOff className="w-3.5 h-3.5 text-gray-500" /> : <Eye className="w-3.5 h-3.5 text-gray-500" />}
                </button>
            </div>
        </div>
    );
}

export function SecretAccessModal({
    isOpen,
    onClose,
    secrets = [],
    secretsToCreate = [],
    onGrant,
    onCreate,
    isGranting,
}) {
    const { t } = useTranslation();
    const [createValues, setCreateValues] = useState({});
    const hasGrants = secrets.length > 0;
    const hasCreates = secretsToCreate.length > 0;

    useEffect(() => {
        if (!isOpen) setCreateValues({});
    }, [isOpen]);

    const handleValueChange = (name, value) => {
        setCreateValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleProceed = () => {
        if (hasCreates && onCreate) {
            const items = secretsToCreate.map((s) => ({
                name: typeof s === "string" ? s : s.name,
                value: createValues[typeof s === "string" ? s : s.name] || "",
                description: typeof s === "string" ? "" : s.reason || "",
            }));
            const allFilled = items.every((i) => i.value.trim());
            if (!allFilled) return;
            onCreate(items);
        }
        if (hasGrants && onGrant) onGrant();
        if (!hasGrants && !hasCreates) onClose();
    };

    const createMissing = hasCreates && secretsToCreate.some(
        (s) => !createValues[typeof s === "string" ? s : s.name]?.trim()
    );

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[460px]">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-full ${hasCreates ? "bg-blue-100" : "bg-amber-100"}`}>
                            {hasCreates ? <Plus className="w-5 h-5 text-blue-600" /> : <Lock className="w-5 h-5 text-amber-600" />}
                        </div>
                        <DialogTitle>
                            {hasCreates
                                ? t("secrets.createRequestTitle", "Secret Required")
                                : t("secrets.accessRequestTitle", "Secret Access Request")}
                        </DialogTitle>
                    </div>
                    <DialogDescription className="pt-2">
                        {hasCreates
                            ? t("secrets.createRequestDescription", "The AI needs the following secrets. Enter the values to create them.")
                            : t("secrets.accessRequestDescription", "The AI is requesting access to the following secrets. Do you want to grant access?")}
                    </DialogDescription>
                </DialogHeader>
                <div className="py-3 space-y-3 max-h-[300px] overflow-y-auto">
                    {hasGrants && (
                        <div className="rounded-md border bg-muted/50 p-3">
                            <ul className="space-y-2">
                                {secrets.map((secret) => (
                                    <li key={secret} className="flex items-center gap-2 text-sm font-medium">
                                        <ShieldCheck className="w-4 h-4 text-green-600 shrink-0" />
                                        <span className="font-mono bg-background px-1.5 py-0.5 rounded border">{secret}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {hasCreates && (
                        <div className="space-y-3">
                            {secretsToCreate.map((s) => {
                                const name = typeof s === "string" ? s : s.name;
                                const reason = typeof s === "string" ? null : s.reason;
                                return (
                                    <div key={name}>
                                        {reason && <p className="text-xs text-muted-foreground mb-1">{reason}</p>}
                                        <SecretValueInput name={name} value={createValues[name] || ""} onChange={handleValueChange} />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isGranting}>{t("common.cancel", "Cancel")}</Button>
                    <Button onClick={handleProceed} disabled={isGranting || createMissing}>
                        {isGranting
                            ? t("common.processing", "Processing...")
                            : hasCreates
                                ? t("secrets.createAndGrant", "Create & Grant")
                                : t("common.grantAccess", "Grant Access")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
